/** Delivery-method ordering + advanced delivery rules — the bottom of the
 *  Delivery methods card, mirroring the dashboard flow:
 *
 *    "If [any|all] of ⟨trigger methods⟩ is ⟨condition⟩, after N min
 *     then send ⟨dependent methods⟩"
 *
 *  Rules are only ACTIVE while ordering is Advanced (2); stored rules are
 *  preserved when the ordering changes (dashboard parity — shown collapsed as
 *  an inactive count). Rows reference this notification's NDM pks; rows for
 *  unsaved (negative-id) methods can't be built — save the method first.
 *  On save the whole set is recreated (ids are client-side keys only).
 */
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Autocomplete,
  Box,
  Button,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import type { AdvancedRuleRow, NotificationDeliveryMethod } from '@/api/notifications';
import {
  ADVANCED_CONDITION_ALL,
  ADVANCED_CONDITION_ANY,
  ADVANCED_CONDITION_LABELS,
  METHOD_ORDERING_ADVANCED,
  METHOD_ORDERING_LABELS,
} from '@/api/notifications';
import { FieldHelp } from '@/components/FieldHelp';

import { ADVANCED_RULES_HELP } from './fieldHelp';
import { methodLabel } from './delivery/DeliveryMethodList';

interface MethodOption {
  id: number;
  label: string;
  unsaved: boolean;
}

let nextClientAdvId = -1;

export function newAdvancedRule(): AdvancedRuleRow {
  return {
    id: nextClientAdvId--,
    condition: 0,
    condition_any_all: ADVANCED_CONDITION_ANY,
    send_after_min: null,
    trigger_notification_delivery_methods: [],
    dependent_notification_delivery_methods: [],
  };
}

interface Props {
  deliveryMethods: NotificationDeliveryMethod[];
  ordering: number;
  onOrderingChange: (value: number) => void;
  rules: AdvancedRuleRow[];
  onRulesChange: (rows: AdvancedRuleRow[]) => void;
  /** Row errors keyed by row index ('*' = general). */
  errors?: Record<string, string>;
  canManage: boolean;
}

export function AdvancedRulesSection({
  deliveryMethods,
  ordering,
  onOrderingChange,
  rules,
  onRulesChange,
  errors,
  canManage,
}: Props) {
  const options: MethodOption[] = deliveryMethods.map((dm) => ({
    id: dm.id,
    label: dm.id > 0 ? `${methodLabel(dm.method)} #${dm.id}` : `${methodLabel(dm.method)} (unsaved)`,
    unsaved: dm.id < 0,
  }));
  const byId = new Map(options.map((o) => [o.id, o]));
  const isAdvanced = ordering === METHOD_ORDERING_ADVANCED;

  const patchRow = (index: number, patch: Partial<AdvancedRuleRow>) =>
    onRulesChange(rules.map((r, i) => (i === index ? { ...r, ...patch } : r)));

  const idsToOptions = (ids: number[]): MethodOption[] =>
    ids.map((id) => byId.get(id) ?? { id, label: `#${id} (removed)`, unsaved: false });

  return (
    <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Method ordering
        </Typography>
        <FieldHelp help={ADVANCED_RULES_HELP.ordering} />
        <ToggleButtonGroup
          exclusive
          size="small"
          value={ordering}
          disabled={!canManage}
          onChange={(_, v) => {
            if (v !== null) onOrderingChange(v as number);
          }}
          aria-label="Delivery method ordering"
        >
          {Object.entries(METHOD_ORDERING_LABELS).map(([value, label]) => (
            <ToggleButton key={value} value={Number(value)} sx={{ px: 1.5, py: 0.25 }}>
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        {!isAdvanced && rules.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            {rules.length} advanced rule{rules.length === 1 ? '' : 's'} configured — inactive while
            ordering is not Advanced.
          </Typography>
        )}
      </Stack>

      {isAdvanced && (
        <Stack spacing={1.5} sx={{ mt: 1.5 }}>
          {errors?.['*'] && (
            <Typography variant="caption" color="error">
              {errors['*']}
            </Typography>
          )}
          {rules.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No advanced rules — with Advanced ordering and no rules, methods that are nobody&apos;s
              dependent are sent immediately and nothing follows up.
            </Typography>
          )}
          {rules.map((rule, index) => {
            const triggers = idsToOptions(rule.trigger_notification_delivery_methods);
            const dependents = idsToOptions(rule.dependent_notification_delivery_methods);
            const rowError = errors?.[String(index)];
            return (
              <Box
                key={rule.id}
                sx={{
                  border: 1,
                  borderColor: rowError ? 'error.main' : 'divider',
                  borderRadius: 1,
                  p: 1.5,
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="body2" color="text.secondary">
                    If
                  </Typography>
                  {rule.trigger_notification_delivery_methods.length > 1 && (
                    <FormControlLabel
                      sx={{ mr: 0 }}
                      control={
                        <Switch
                          size="small"
                          checked={rule.condition_any_all === ADVANCED_CONDITION_ALL}
                          disabled={!canManage}
                          onChange={(e) =>
                            patchRow(index, {
                              condition_any_all: e.target.checked
                                ? ADVANCED_CONDITION_ALL
                                : ADVANCED_CONDITION_ANY,
                            })
                          }
                        />
                      }
                      label={
                        <Typography variant="caption">
                          {rule.condition_any_all === ADVANCED_CONDITION_ALL ? 'all of' : 'any of'}
                        </Typography>
                      }
                    />
                  )}
                  <Autocomplete<MethodOption, true>
                    multiple
                    size="small"
                    sx={{ minWidth: 240, flex: 1 }}
                    disabled={!canManage}
                    options={options}
                    getOptionLabel={(o) => o.label}
                    getOptionDisabled={(o) =>
                      o.unsaved || rule.dependent_notification_delivery_methods.includes(o.id)
                    }
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    value={triggers}
                    onChange={(_, v) =>
                      patchRow(index, { trigger_notification_delivery_methods: v.map((o) => o.id) })
                    }
                    renderInput={(params) => <TextField {...params} label="These methods" />}
                  />
                  <TextField
                    select
                    size="small"
                    label="Is"
                    sx={{ width: 160 }}
                    value={rule.condition}
                    disabled={!canManage}
                    onChange={(e) => patchRow(index, { condition: Number(e.target.value) })}
                  >
                    {Object.entries(ADVANCED_CONDITION_LABELS).map(([value, label]) => (
                      <MenuItem key={value} value={Number(value)}>
                        {label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    size="small"
                    type="number"
                    label="After"
                    sx={{ width: 120 }}
                    value={rule.send_after_min ?? ''}
                    disabled={!canManage}
                    inputProps={{ min: 0 }}
                    InputProps={{ endAdornment: <Typography variant="caption">min</Typography> }}
                    onChange={(e) => {
                      const raw = e.target.value;
                      patchRow(index, {
                        send_after_min: raw === '' ? null : Math.max(0, Number(raw) || 0),
                      });
                    }}
                  />
                  <Autocomplete<MethodOption, true>
                    multiple
                    size="small"
                    sx={{ minWidth: 240, flex: 1 }}
                    disabled={!canManage}
                    options={options}
                    getOptionLabel={(o) => o.label}
                    getOptionDisabled={(o) =>
                      o.unsaved || rule.trigger_notification_delivery_methods.includes(o.id)
                    }
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    value={dependents}
                    onChange={(_, v) =>
                      patchRow(index, {
                        dependent_notification_delivery_methods: v.map((o) => o.id),
                      })
                    }
                    renderInput={(params) => <TextField {...params} label="Then send" />}
                  />
                  <IconButton
                    size="small"
                    aria-label="Remove advanced rule"
                    disabled={!canManage}
                    onClick={() => onRulesChange(rules.filter((_, i) => i !== index))}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
                {rowError && (
                  <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                    {rowError}
                  </Typography>
                )}
              </Box>
            );
          })}
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              startIcon={<AddIcon />}
              disabled={!canManage}
              onClick={() => onRulesChange([...rules, newAdvancedRule()])}
            >
              Add advanced rule
            </Button>
            <FieldHelp help={ADVANCED_RULES_HELP.rule} />
          </Stack>
        </Stack>
      )}
    </Box>
  );
}
